import { isSportsBetCartTerminalSelectionMismatch } from './sportsBetCartSelectionStatus.js';
import { sportsnewOddStateIdOf } from './sportsnewOddStateId.js';
import {
  SPORTSNEW_SELECTED_RECOVERY_LIMIT,
  normalizeSelectedRecoveryIds,
  normalizeSelectedRecoveryResult,
} from './sportsnewSelectedRecoveryNormalization.js';
import {
  selectedPatchFreshness,
  shouldPreserveExistingSelectedUpdate,
  shouldPreserveNewerSelectedWs,
} from './sportsnewSelectedRecoveryPolicy.js';

export {
  SPORTSNEW_SELECTED_RECOVERY_LIMIT,
  normalizeSelectedRecoveryIds,
  normalizeSelectedRecoveryResult,
  selectedPatchFreshness,
  shouldPreserveExistingSelectedUpdate,
  shouldPreserveNewerSelectedWs,
};

export const selectedRecoveryRequestKey = (oddStateIds, displayScope = '', recoveryOwnerKey = '') => (
  `${String(recoveryOwnerKey || '').trim()}|${String(displayScope || '').trim().toLowerCase()}|${normalizeSelectedRecoveryIds(oddStateIds).join(',')}`
);

export const applyAuthoritativeSelectedUpdates = ({
  authoritativeUpdates = [],
  requestedValues = [],
  freshnessById,
  isSelectionActive = () => true,
  applyUpdate = () => {},
} = {}) => {
  const requestedIds = new Set(normalizeSelectedRecoveryIds(requestedValues));
  const freshness = freshnessById instanceof Map ? freshnessById : new Map();
  const appliedOddStateIds = [];
  const skippedNewerOddStateIds = [];
  const skippedInactiveOddStateIds = [];
  const seenOddStateIds = new Set();
  let actionableCount = 0;

  (Array.isArray(authoritativeUpdates) ? authoritativeUpdates : []).forEach((update) => {
    const oddStateId = sportsnewOddStateIdOf(update);
    if (
      oddStateId == null
      || !requestedIds.has(oddStateId)
      || seenOddStateIds.has(oddStateId)
    ) return;
    seenOddStateIds.add(oddStateId);

    if (!isSelectionActive(oddStateId)) {
      skippedInactiveOddStateIds.push(oddStateId);
      return;
    }
    const updateFreshness = selectedPatchFreshness(update, null);
    const preserveExistingFreshness = shouldPreserveNewerSelectedWs(
      updateFreshness,
      null,
      freshness.get(oddStateId),
    );
    const terminalSelectionMismatch = isSportsBetCartTerminalSelectionMismatch(
      update?.patch?.reason,
    );
    if (preserveExistingFreshness && !terminalSelectionMismatch) {
      skippedNewerOddStateIds.push(oddStateId);
      return;
    }

    const outcome = applyUpdate(update, {
      preserveExistingFreshness: preserveExistingFreshness && terminalSelectionMismatch,
    });
    appliedOddStateIds.push(oddStateId);
    if (outcome?.requiresAcceptance === true) actionableCount += 1;
  });

  return {
    appliedOddStateIds,
    skippedNewerOddStateIds,
    skippedInactiveOddStateIds,
    actionableCount,
  };
};

export const recoverSelectedOddsCoalesced = ({
  inFlightRequests,
  submittedSelections,
  displayScope,
  recoverSelected,
  wsFreshnessById,
  recoveryOwnerKey = '',
  isSelectionActive = () => true,
  applyUpdate = () => {},
  isCancelled = () => false,
}) => {
  const oddStateIds = normalizeSelectedRecoveryIds(submittedSelections);
  if (!(inFlightRequests instanceof Map) || typeof recoverSelected !== 'function' || oddStateIds.length === 0) {
    return Promise.resolve({ applied: false, reason: 'no_selected_odds', oddStateIds });
  }

  const requestKey = selectedRecoveryRequestKey(oddStateIds, displayScope, recoveryOwnerKey);
  const existingRequest = inFlightRequests.get(requestKey);
  if (existingRequest) return existingRequest;

  const freshness = wsFreshnessById instanceof Map ? wsFreshnessById : new Map();
  const wsFreshnessAtRequest = new Map(oddStateIds.map((oddStateId) => [
    oddStateId,
    freshness.get(oddStateId),
  ]));

  const request = (async () => {
    try {
      const response = await recoverSelected({ oddStateIds, displayScope });
      if (isCancelled()) return { applied: false, reason: 'cancelled', oddStateIds };
      if (response?.status !== true) {
        return { applied: false, reason: 'request_failed', oddStateIds, response };
      }

      const normalized = normalizeSelectedRecoveryResult(response, oddStateIds);
      const skippedOddStateIds = [];
      const skippedInactiveOddStateIds = [];
      const appliedOddStateIds = [];
      const actionableAppliedOddStateIds = [];
      normalized.updates.forEach((update) => {
        if (isCancelled()) return;
        const { oddStateId } = update;
        if (!isSelectionActive(oddStateId)) {
          skippedInactiveOddStateIds.push(oddStateId);
          return;
        }
        if (shouldPreserveNewerSelectedWs(
          update,
          wsFreshnessAtRequest.get(oddStateId),
          freshness.get(oddStateId),
        )) {
          skippedOddStateIds.push(oddStateId);
          return;
        }
        if (isCancelled()) return;
        const outcome = applyUpdate(update);
        appliedOddStateIds.push(oddStateId);
        if (outcome?.requiresAcceptance === true) {
          actionableAppliedOddStateIds.push(oddStateId);
        }
      });

      if (isCancelled()) return { applied: false, reason: 'cancelled', oddStateIds };

      return {
        applied: true,
        reason: 'selected_rest_refresh',
        oddStateIds,
        missingOddStateIds: normalized.missingOddStateIds,
        appliedOddStateIds,
        actionableAppliedOddStateIds,
        skippedOddStateIds,
        skippedInactiveOddStateIds,
      };
    } catch (error) {
      return { applied: false, reason: 'request_error', oddStateIds, error };
    }
  })();

  inFlightRequests.set(requestKey, request);
  request.finally(() => {
    if (inFlightRequests.get(requestKey) === request) inFlightRequests.delete(requestKey);
  });
  return request;
};

export { sportsnewOddStateIdOf as selectedRecoveryOddStateIdOf } from './sportsnewOddStateId.js';
